// idleSessionManager.js
//
// "Log out after 15 minutes of real inactivity; otherwise keep the session
// fresh" -- two halves of the same mechanism:
//   1. Track genuine user activity (mouse/keyboard/touch/scroll), shared
//      across tabs via localStorage so being active in one tab doesn't get
//      you logged out of another.
//   2. While active, proactively refresh the access token on an interval
//      comfortably shorter than its 30-minute expiry, so an actively-used
//      session never hits a jarring reactive 401->refresh round trip and
//      never approaches expiry. Idle time is never masked by this --
//      the refresh interval and the idle check are independent.

import authService from '../authService';
import store from '../../../../store';
import { logoutUser, clearAuth } from '../authslice';

const ACTIVITY_KEY = 'stocker-last-activity';
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_BEFORE_MS = 60 * 1000; // warn 60s before the actual logout
const CHECK_INTERVAL_MS = 15 * 1000; // poll cadence
const KEEP_ALIVE_INTERVAL_MS = 10 * 60 * 1000; // refresh every 10 min while active (access token lasts 30)
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000; // don't hammer localStorage on every mousemove

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'touchstart', 'scroll'];

let checkIntervalId = null;
let keepAliveIntervalId = null;
let lastWriteAt = 0;
let warningShown = false;
let onWarnCallback = null;
let onWarnCancelCallback = null;

function now() {
  return Date.now();
}

function getLastActivity() {
  const raw = window.localStorage.getItem(ACTIVITY_KEY);
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) ? parsed : now();
}

function recordActivity() {
  const t = now();
  if (t - lastWriteAt < ACTIVITY_WRITE_THROTTLE_MS) return;
  lastWriteAt = t;
  window.localStorage.setItem(ACTIVITY_KEY, String(t));
  if (warningShown) {
    warningShown = false;
    onWarnCancelCallback?.();
  }
}

function attachActivityListeners() {
  ACTIVITY_EVENTS.forEach((evt) =>
    window.addEventListener(evt, recordActivity, { passive: true })
  );
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') recordActivity();
  });
}

function detachActivityListeners() {
  ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, recordActivity));
}

async function forceIdleLogout() {
  try {
    await store.dispatch(logoutUser());
  } catch {
    // best-effort -- clear local state regardless of whether the API call succeeded
  }
  store.dispatch(clearAuth());

  const currentPath = window.location.pathname + window.location.search;
  const ret = encodeURIComponent(currentPath);
  window.location.assign(`/login?session=idle_timeout&returnTo=${ret}`);
}

function checkIdle() {
  const isAuthenticated = !!store.getState()?.auth?.isAuthenticated;
  if (!isAuthenticated) return;

  const idleFor = now() - getLastActivity();

  if (idleFor >= IDLE_TIMEOUT_MS) {
    stopIdleSessionMonitor();
    forceIdleLogout();
    return;
  }

  const remaining = IDLE_TIMEOUT_MS - idleFor;
  if (remaining <= WARNING_BEFORE_MS && !warningShown) {
    warningShown = true;
    onWarnCallback?.(Math.ceil(remaining / 1000));
  }
}

async function keepAlive() {
  const isAuthenticated = !!store.getState()?.auth?.isAuthenticated;
  if (!isAuthenticated) return;

  // Only refresh if the user has actually been active recently -- an idle
  // session should be left to expire on schedule, not artificially kept alive.
  const idleFor = now() - getLastActivity();
  if (idleFor >= IDLE_TIMEOUT_MS) return;

  try {
    await authService.refreshToken();
  } catch (e) {
    // Non-fatal here -- the reactive 401 interceptor is still the safety net
    // if this proactive refresh fails for a transient reason.
    console.warn('[IdleSessionManager] Proactive keep-alive refresh failed:', e?.message);
  }
}

/**
 * Starts idle tracking + keep-alive for the current session.
 * @param {object} callbacks
 * @param {(secondsRemaining: number) => void} callbacks.onWarn - called once, ~60s before idle logout
 * @param {() => void} callbacks.onWarnCancel - called if activity resumes after a warning was shown
 */
export function startIdleSessionMonitor({ onWarn, onWarnCancel } = {}) {
  if (checkIntervalId) return; // already running

  onWarnCallback = onWarn || null;
  onWarnCancelCallback = onWarnCancel || null;
  warningShown = false;

  recordActivity();
  attachActivityListeners();

  checkIntervalId = window.setInterval(checkIdle, CHECK_INTERVAL_MS);
  keepAliveIntervalId = window.setInterval(keepAlive, KEEP_ALIVE_INTERVAL_MS);
}

export function stopIdleSessionMonitor() {
  detachActivityListeners();
  if (checkIntervalId) {
    window.clearInterval(checkIntervalId);
    checkIntervalId = null;
  }
  if (keepAliveIntervalId) {
    window.clearInterval(keepAliveIntervalId);
    keepAliveIntervalId = null;
  }
  warningShown = false;
}

/** Explicitly mark activity right now -- e.g. after the user dismisses the idle warning. */
export function markActiveNow() {
  lastWriteAt = 0; // bypass throttle for an explicit user action
  recordActivity();
}
